import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { subDays, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
// DTC-K: route the shared date-range filter "now" anchor through the
// canonical clock so analytics windows are deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

export type DateRangeOption = '7d' | '30d' | '90d' | 'ytd' | 'all';

interface DateRangeFilterProps {
  value: DateRangeOption;
  onChange: (value: DateRangeOption) => void;
  className?: string;
}

export function DateRangeFilter({ value, onChange, className }: DateRangeFilterProps) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground">Date Range</Label>
      <Select value={value} onValueChange={(v) => onChange(v as DateRangeOption)}>
        <SelectTrigger className="w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="7d">Last 7 Days</SelectItem>
          <SelectItem value="30d">Last 30 Days</SelectItem>
          <SelectItem value="90d">Last 90 Days</SelectItem>
          <SelectItem value="ytd">Year to Date</SelectItem>
          <SelectItem value="all">All Time</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Get start and end dates based on the selected range option.
 */
export function getDateRange(option: DateRangeOption, shopTimezone: string): { startDate: string; endDate: string } {
  const nowZoned = toZonedTime(clock.now(), shopTimezone);
  const endDate = format(nowZoned, 'yyyy-MM-dd');
  
  switch (option) {
    case '7d':
      return { startDate: format(subDays(nowZoned, 7), 'yyyy-MM-dd'), endDate };
    case '30d':
      return { startDate: format(subDays(nowZoned, 30), 'yyyy-MM-dd'), endDate };
    case '90d':
      return { startDate: format(subDays(nowZoned, 90), 'yyyy-MM-dd'), endDate };
    case 'ytd':
      return { startDate: `${nowZoned.getFullYear()}-01-01`, endDate };
    case 'all':
      return { startDate: '2020-01-01', endDate };
    default:
      return { startDate: format(subDays(nowZoned, 30), 'yyyy-MM-dd'), endDate };
  }
}
